/**
 * Bilingual email templates for Lavvissa
 * Each template returns an HTML string with inline CSS.
 * Persian (FA) is primary, English (EN) is secondary.
 */

const BRAND = {
  name: 'Lavvissa',
  logoUrl: 'https://lavvissa.com/logo.png',
  siteUrl: 'https://lavvissa.com',
  supportEmail: 'support@lavvissa.com',
  primaryColor: '#1a1a2e',
  accentColor: '#e94560',
  bgColor: '#f8f9fa',
  textColor: '#333333',
  lightText: '#888888',
};

// Shared layout wrapper
function layout(content, preheader = '') {
  return `<!DOCTYPE html>
<html lang="fa" dir="rtl">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>${BRAND.name}</title>
  <!--[if mso]>
  <noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript>
  <![endif]-->
  <style>
    body, table, td, a { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { -ms-interpolation-mode: bicubic; border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; }
    body { margin: 0; padding: 0; width: 100% !important; height: 100% !important; }
    @media only screen and (max-width: 600px) {
      .container { width: 100% !important; padding: 16px !important; }
      .content-cell { padding: 24px 16px !important; }
      .header-cell { padding: 20px 16px !important; }
      .item-row td { display: block !important; width: 100% !important; text-align: right !important; }
    }
  </style>
</head>
<body style="margin:0; padding:0; background-color:${BRAND.bgColor}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, Arial, sans-serif;">
  ${preheader ? `<div style="display:none;font-size:1px;color:${BRAND.bgColor};line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;">${preheader}</div>` : ''}
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background-color:${BRAND.bgColor};">
    <tr>
      <td align="center" style="padding: 24px 16px;">
        <table role="presentation" cellpadding="0" cellspacing="0" width="600" class="container" style="max-width:600px; width:100%; background-color:#ffffff; border-radius:12px; overflow:hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <!-- Header -->
          <tr>
            <td class="header-cell" style="background-color:${BRAND.primaryColor}; padding:28px 32px; text-align:center;">
              <img src="${BRAND.logoUrl}" alt="${BRAND.name}" width="140" style="max-width:140px; height:auto;" />
            </td>
          </tr>
          <!-- Content -->
          <tr>
            <td class="content-cell" style="padding:32px; direction:rtl; text-align:right; color:${BRAND.textColor}; font-size:15px; line-height:1.7;">
              ${content}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding:24px 32px; background-color:#fafafa; border-top:1px solid #eee; text-align:center; font-size:12px; color:${BRAND.lightText}; line-height:1.6;">
              <p style="margin:0 0 4px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, Arial, sans-serif;">
                ${BRAND.name} &mdash; مد و استایل بین‌المللی
              </p>
              <p style="margin:0 0 4px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, Arial, sans-serif;">
                International Fashion & Style
              </p>
              <p style="margin:8px 0 0 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, Arial, sans-serif;">
                <a href="${BRAND.siteUrl}" style="color:${BRAND.accentColor}; text-decoration:none;">${BRAND.siteUrl}</a>
                &nbsp;|&nbsp;
                <a href="mailto:${BRAND.supportEmail}" style="color:${BRAND.accentColor}; text-decoration:none;">${BRAND.supportEmail}</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

function btn(text, href) {
  return `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:24px auto;">
  <tr>
    <td style="background-color:${BRAND.accentColor}; border-radius:8px; text-align:center;">
      <a href="${href}" style="display:inline-block; padding:14px 36px; color:#ffffff; text-decoration:none; font-size:15px; font-weight:600; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, Arial, sans-serif;">${text}</a>
    </td>
  </tr>
</table>`;
}

function divider() {
  return `<hr style="border:none; border-top:1px solid #eee; margin:24px 0;" />`;
}

// ── 1. Order Confirmation ──────────────────────────────────────────

export function orderConfirmation(order, user) {
  const displayName = user?.display_name || user?.email || 'کاربر';
  const orderNumber = order?.order_number || order?.id || '—';
  const items = order?.items || [];
  const totals = order?.totals || {};

  const itemRows = items.map(item => `
    <tr class="item-row">
      <td style="padding:8px 0; border-bottom:1px solid #f0f0f0; font-size:14px;">
        ${item.name || '—'}
        ${item.color ? ` — ${item.color}` : ''}${item.size ? ` / ${item.size}` : ''}
      </td>
      <td style="padding:8px 0; border-bottom:1px solid #f0f0f0; text-align:left; font-size:14px; white-space:nowrap;">
        ${item.quantity || 1} &times; ${Number(item.salePrice || item.price || 0).toLocaleString('fa-IR')} تومان
      </td>
    </tr>`).join('');

  const content = `
    <h2 style="margin:0 0 8px; font-size:20px; color:${BRAND.primaryColor};">سفارش شما با موفقیت ثبت شد</h2>
    <p style="margin:0 0 4px; color:${BRAND.lightText}; font-size:13px;">Your order has been placed successfully</p>
    ${divider()}
    <p>سلام <strong>${displayName}</strong>،</p>
    <p>سفارش شما با شماره <strong style="color:${BRAND.accentColor};">${orderNumber}</strong> ثبت شد و در حال پردازش است.</p>

    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin:16px 0;">
      <thead>
        <tr>
          <th style="text-align:right; padding:8px 0; border-bottom:2px solid ${BRAND.primaryColor}; font-size:13px;">محصول</th>
          <th style="text-align:left; padding:8px 0; border-bottom:2px solid ${BRAND.primaryColor}; font-size:13px;">قیمت</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
      </tbody>
    </table>

    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin:16px 0;">
      <tr>
        <td style="padding:4px 0; font-size:14px;">جمع کل:</td>
        <td style="padding:4px 0; text-align:left; font-size:14px;">${Number(totals.subtotal || 0).toLocaleString('fa-IR')} تومان</td>
      </tr>
      ${totals.shipping ? `<tr>
        <td style="padding:4px 0; font-size:14px;">هزینه ارسال:</td>
        <td style="padding:4px 0; text-align:left; font-size:14px;">${Number(totals.shipping).toLocaleString('fa-IR')} تومان</td>
      </tr>` : ''}
      ${totals.discount ? `<tr>
        <td style="padding:4px 0; font-size:14px; color:#28a745;">تخفیف:</td>
        <td style="padding:4px 0; text-align:left; font-size:14px; color:#28a745;">-${Number(totals.discount).toLocaleString('fa-IR')} تومان</td>
      </tr>` : ''}
      <tr>
        <td style="padding:8px 0; font-size:16px; font-weight:700; border-top:2px solid ${BRAND.primaryColor};">مبلغ نهایی:</td>
        <td style="padding:8px 0; text-align:left; font-size:16px; font-weight:700; border-top:2px solid ${BRAND.primaryColor}; color:${BRAND.accentColor};">${Number(totals.total || 0).toLocaleString('fa-IR')} تومان</td>
      </tr>
    </table>

    ${btn('پیگیری سفارش / Track Order', `${BRAND.siteUrl}/orders/${order?.id || ''}`)}

    <p style="font-size:13px; color:${BRAND.lightText}; text-align:center;">در صورت هرگونه سوال با ما تماس بگیرید | Contact us for any questions</p>
  `;

  return {
    subject: `سفارش ${orderNumber} ثبت شد | Order Confirmed — ${BRAND.name}`,
    html: layout(content, `سفارش ${orderNumber} با موفقیت ثبت شد`),
  };
}

// ── 2. Shipping Update ────────────────────────────────────────────

export function shippingUpdate(order, trackingInfo, user) {
  const displayName = user?.display_name || user?.email || 'کاربر';
  const orderNumber = order?.order_number || order?.id || '—';
  const trackingCode = trackingInfo?.trackingCode || trackingInfo?.code || '—';
  const carrier = trackingInfo?.carrier || 'پست';
  const estimatedDelivery = trackingInfo?.estimatedDelivery || '';
  const trackingUrl = trackingInfo?.trackingUrl || '';

  const content = `
    <h2 style="margin:0 0 8px; font-size:20px; color:${BRAND.primaryColor};">سفارش شما ارسال شد</h2>
    <p style="margin:0 0 4px; color:${BRAND.lightText}; font-size:13px;">Your order has been shipped</p>
    ${divider()}
    <p>سلام <strong>${displayName}</strong>،</p>
    <p>سفارش <strong style="color:${BRAND.accentColor};">${orderNumber}</strong> ارسال شد.</p>

    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin:16px 0; background-color:#f8f9fa; border-radius:8px; padding:16px;">
      <tr>
        <td style="padding:16px;">
          <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
            <tr>
              <td style="padding:4px 0; font-size:14px; font-weight:600;">کد رهگیری:</td>
              <td style="padding:4px 0; text-align:left; font-size:14px; font-family:monospace; letter-spacing:1px;">${trackingCode}</td>
            </tr>
            <tr>
              <td style="padding:4px 0; font-size:14px; font-weight:600;">شرکت حمل:</td>
              <td style="padding:4px 0; text-align:left; font-size:14px;">${carrier}</td>
            </tr>
            ${estimatedDelivery ? `<tr>
              <td style="padding:4px 0; font-size:14px; font-weight:600;">تحویل تخمینی:</td>
              <td style="padding:4px 0; text-align:left; font-size:14px;">${estimatedDelivery}</td>
            </tr>` : ''}
          </table>
        </td>
      </tr>
    </table>

    ${trackingUrl ? btn('رهگیری مرسوله / Track Shipment', trackingUrl) : btn('پیگیری سفارش / Track Order', `${BRAND.siteUrl}/orders/${order?.id || ''}`)}

    <p style="font-size:13px; color:${BRAND.lightText}; text-align:center;">Tracking Code: ${trackingCode} | Carrier: ${carrier}</p>
  `;

  return {
    subject: `سفارش ${orderNumber} ارسال شد | Shipped — ${BRAND.name}`,
    html: layout(content, `سفارش ${orderNumber} ارسال شد`),
  };
}

// ── 3. Wallet Charged (Approved) ──────────────────────────────────

export function walletCharged(amount, user) {
  const displayName = user?.display_name || user?.email || 'کاربر';
  const formattedAmount = Number(amount || 0).toLocaleString('fa-IR');

  const content = `
    <h2 style="margin:0 0 8px; font-size:20px; color:${BRAND.primaryColor};">کیف پول شما شارژ شد</h2>
    <p style="margin:0 0 4px; color:${BRAND.lightText}; font-size:13px;">Your wallet has been charged</p>
    ${divider()}
    <p>سلام <strong>${displayName}</strong>،</p>
    <p>مبلغ <strong style="color:#28a745; font-size:18px;">${formattedAmount} تومان</strong> با موفقیت به کیف پول شما اضافه شد.</p>

    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin:16px 0; background-color:#f0fff4; border-radius:8px; border:1px solid #c6f6d5;">
      <tr>
        <td style="padding:20px; text-align:center;">
          <p style="margin:0; font-size:14px; color:#276749;">مبلغ شارژ شده / Amount Charged</p>
          <p style="margin:8px 0 0; font-size:28px; font-weight:700; color:#28a745;">${formattedAmount}</p>
          <p style="margin:0; font-size:13px; color:#276749;">تومان</p>
        </td>
      </tr>
    </table>

    ${btn('مشاهده کیف پول / View Wallet', `${BRAND.siteUrl}/profile`)}

    <p style="font-size:13px; color:${BRAND.lightText}; text-align:center;">اکنون می‌توانید از موجودی کیف پول خود برای خرید استفاده کنید.</p>
    <p style="font-size:12px; color:${BRAND.lightText}; text-align:center;">You can now use your wallet balance for purchases.</p>
  `;

  return {
    subject: `کیف پول شارژ شد: ${formattedAmount} تومان | Wallet Charged — ${BRAND.name}`,
    html: layout(content, `${formattedAmount} تومان به کیف پول شما اضافه شد`),
  };
}

// ── 4. Wallet Rejected ────────────────────────────────────────────

export function walletRejected(amount, reason, user) {
  const displayName = user?.display_name || user?.email || 'کاربر';
  const formattedAmount = Number(amount || 0).toLocaleString('fa-IR');

  const content = `
    <h2 style="margin:0 0 8px; font-size:20px; color:${BRAND.primaryColor};">درخواست شارژ کیف پول رد شد</h2>
    <p style="margin:0 0 4px; color:${BRAND.lightText}; font-size:13px;">Your wallet charge request was rejected</p>
    ${divider()}
    <p>سلام <strong>${displayName}</strong>،</p>
    <p>متاسفانه درخواست شارژ <strong>${formattedAmount} تومان</strong> به کیف پول شما تایید نشد.</p>

    ${reason ? `
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin:16px 0; background-color:#fff5f5; border-radius:8px; border:1px solid #fed7d7;">
      <tr>
        <td style="padding:16px;">
          <p style="margin:0 0 4px; font-size:13px; font-weight:600; color:#c53030;">دلیل / Reason:</p>
          <p style="margin:0; font-size:14px; color:#742a2a;">${reason}</p>
        </td>
      </tr>
    </table>` : ''}

    <p>در صورت نیاز به اطلاعات بیشتر با پشتیبانی تماس بگیرید.</p>
    <p style="font-size:13px; color:${BRAND.lightText};">If you need more information, please contact support.</p>

    ${btn('تماس با پشتیبانی / Contact Support', `mailto:${BRAND.supportEmail}`)}
  `;

  return {
    subject: `درخواست شارژ رد شد | Wallet Request Rejected — ${BRAND.name}`,
    html: layout(content, `درخواست شارژ ${formattedAmount} تومان رد شد`),
  };
}

// ── 5. Password Reset ─────────────────────────────────────────────

export function passwordReset(resetLink, user) {
  const displayName = user?.display_name || user?.email || 'کاربر';

  const content = `
    <h2 style="margin:0 0 8px; font-size:20px; color:${BRAND.primaryColor};">بازیابی رمز عبور</h2>
    <p style="margin:0 0 4px; color:${BRAND.lightText}; font-size:13px;">Password Reset Request</p>
    ${divider()}
    <p>سلام <strong>${displayName}</strong>،</p>
    <p>درخواست بازیابی رمز عبور برای حساب شما ارسال شده است. برای تغییر رمز عبور روی دکمه زیر کلیک کنید:</p>

    ${btn('تغییر رمز عبور / Reset Password', resetLink)}

    <p style="font-size:13px; color:${BRAND.lightText};">این لینک تا ۶۰ دقیقه معتبر است.</p>
    <p style="font-size:13px; color:${BRAND.lightText};">This link is valid for 60 minutes.</p>

    ${divider()}

    <p style="font-size:12px; color:${BRAND.lightText};">اگر شما این درخواست را ارسال نکرده‌اید، این ایمیل را نادیده بگیرید.</p>
    <p style="font-size:12px; color:${BRAND.lightText};">If you did not request this, please ignore this email.</p>

    <p style="font-size:11px; color:#ccc; word-break:break-all; margin-top:16px;">
      لینک: ${resetLink}
    </p>
  `;

  return {
    subject: `بازیابی رمز عبور | Password Reset — ${BRAND.name}`,
    html: layout(content, 'درخواست بازیابی رمز عبور'),
  };
}

// ── 6. Welcome Email ──────────────────────────────────────────────

export function welcomeEmail(user) {
  const displayName = user?.display_name || user?.user_metadata?.full_name || user?.email || 'کاربر';

  const content = `
    <div style="text-align:center; margin-bottom:24px;">
      <h2 style="margin:0 0 8px; font-size:24px; color:${BRAND.primaryColor};">به لاویسا خوش آمدید</h2>
      <p style="margin:0; color:${BRAND.lightText}; font-size:14px;">Welcome to Lavvissa</p>
    </div>
    ${divider()}
    <p>سلام <strong>${displayName}</strong>،</p>
    <p>از عضویت شما در خانواده <strong>${BRAND.name}</strong> خوشحالیم! ما بهترین محصولات مد بین‌المللی را برای شما گردآوری کرده‌ایم.</p>

    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin:20px 0;">
      <tr>
        <td width="33%" style="text-align:center; padding:12px; vertical-align:top;">
          <div style="font-size:28px; margin-bottom:8px;">&#128722;</div>
          <p style="margin:0; font-size:13px; font-weight:600; color:${BRAND.primaryColor};">مرور محصولات</p>
          <p style="margin:4px 0 0; font-size:12px; color:${BRAND.lightText};">Browse Products</p>
        </td>
        <td width="33%" style="text-align:center; padding:12px; vertical-align:top;">
          <div style="font-size:28px; margin-bottom:8px;">&#128176;</div>
          <p style="margin:0; font-size:13px; font-weight:600; color:${BRAND.primaryColor};">کیف پول</p>
          <p style="margin:4px 0 0; font-size:12px; color:${BRAND.lightText};">Wallet System</p>
        </td>
        <td width="33%" style="text-align:center; padding:12px; vertical-align:top;">
          <div style="font-size:28px; margin-bottom:8px;">&#128230;</div>
          <p style="margin:0; font-size:13px; font-weight:600; color:${BRAND.primaryColor};">ارسال سریع</p>
          <p style="margin:4px 0 0; font-size:12px; color:${BRAND.lightText};">Fast Shipping</p>
        </td>
      </tr>
    </table>

    ${btn('شروع خرید / Start Shopping', BRAND.siteUrl)}

    <p style="font-size:13px; color:${BRAND.lightText}; text-align:center;">
      We're excited to have you! Explore the latest fashion from international brands.
    </p>
  `;

  return {
    subject: `خوش آمدید به ${BRAND.name} | Welcome — ${BRAND.name}`,
    html: layout(content, `خوش آمدید به ${BRAND.name}`),
  };
}

// ── 7. Marketing Email ────────────────────────────────────────────

export function marketingEmail(campaign, user) {
  const displayName = user?.display_name || user?.email || 'کاربر';
  const title = campaign?.title || { fa: 'پیشنهاد ویژه', en: 'Special Offer' };
  const body = campaign?.body || { fa: '', en: '' };
  const ctaText = campaign?.ctaText || { fa: 'مشاهده', en: 'View' };
  const ctaUrl = campaign?.ctaUrl || BRAND.siteUrl;
  const heroImage = campaign?.heroImage || '';

  const content = `
    ${heroImage ? `<img src="${heroImage}" alt="${title.fa}" style="width:100%; max-width:536px; height:auto; border-radius:8px; margin-bottom:16px;" />` : ''}
    <h2 style="margin:0 0 8px; font-size:22px; color:${BRAND.primaryColor};">${title.fa}</h2>
    <p style="margin:0 0 4px; color:${BRAND.lightText}; font-size:13px;">${title.en}</p>
    ${divider()}
    <p>سلام <strong>${displayName}</strong>،</p>
    ${body.fa ? `<p>${body.fa}</p>` : ''}
    ${body.en ? `<p style="font-size:13px; color:${BRAND.lightText};">${body.en}</p>` : ''}

    ${btn(`${ctaText.fa} / ${ctaText.en}`, ctaUrl)}

    ${divider()}
    <p style="font-size:11px; color:#ccc; text-align:center;">
      برای لغو اشتراک <a href="${BRAND.siteUrl}/unsubscribe" style="color:${BRAND.accentColor};">اینجا</a> کلیک کنید
      &nbsp;|&nbsp;
      <a href="${BRAND.siteUrl}/unsubscribe" style="color:${BRAND.accentColor};">Unsubscribe</a>
    </p>
  `;

  return {
    subject: `${title.fa} | ${title.en} — ${BRAND.name}`,
    html: layout(content, title.fa),
  };
}
